import { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { CycleI, PackName } from '~src/interfaces/cycle.interface';
import { AnswerI, AnswerValue, CardI, Question } from '~src/interfaces/question.interface';
import { deleteAnswer, fetchQuestions, updateQuestion } from '~src/modules/questions/actions/actions';
import MobileTongue from '~src/modules/questions/components/MobileTongue';
import PopoverInfo from '~src/modules/questions/components/PopoverInfo';
import QuestionsInfo from '~src/modules/questions/components/QuestionsInfo';
import LoadingCard from '~src/modules/questions/components/cards/LoadingCard';
import QuestionCard from '~src/modules/questions/components/cards/QuestionCard';
import { getQuestions } from '~src/modules/questions/reducers/questionsReducer';
import { StoreI } from '~src/rootReducer';
import { ApiReduxResp, isSuccess } from '~src/services/apiMiddleware';
import logger from '~src/services/logger/LlsLogger';
import { translateContent } from '~src/services/translationServices';
import StreamAnimation from '~src/shared/streamAnimation/StreamAnimation';
import { Mobile } from '~src/styles/containers.sc';

import { ScaleVisualization } from '~src/interfaces/scale.interface';
import { ScalesState } from '~src/modules/scales/reducers/scales.reducer';
import { QuestionsGrid, QuestionsWrapper } from './questions.sc';

interface OwnProps {
  self?: boolean;
  finishQS: (pack: PackName) => void;
  cycle: CycleI;
  precedent?: string;
}

interface DispatchProps {
  fetchCards: (id: string, filter: PackName, precedent?: string) => ApiReduxResp<Question[]>;
  updateCard: (id: string, answer: AnswerI) => ApiReduxResp<AnswerI>;
  deleteAnswer: (id: string) => ApiReduxResp<void>;
}

interface StateProps {
  cards: CardI[];
  scales: ScalesState['byId'];
  cardsLoading: boolean;
}

type Props = OwnProps & DispatchProps & StateProps;

interface State {
  tmpComment: string;
  commentChanged: boolean;
}

const SUBMIT_MINIMUM_DELAY = 1000;

const AssessmentStream: FC<Props> = (props: Props) => {
  const [state, setState] = useState<State>({
    tmpComment: '',
    commentChanged: false,
  });

  useEffect(() => {
    const doAsyncStuff = async () => {
      await props.fetchCards(
        props.cycle.id,
        props.self ? PackName.SelfAssessment : PackName.Assessment,
        props.precedent,
      );
    };
    void doAsyncStuff();
  }, []);

  const getStartIndex = () => {
    if (props.precedent) {
      const precedentIndex = props.cards.findIndex((card) => {
        return card.about && card.about.id === props.precedent;
      });

      if (precedentIndex !== 0) {
        logger.warn('precedent is not first card but at index ' + precedentIndex);
      }

      return precedentIndex === -1 ? 0 : precedentIndex;
    }

    const firstUnansweredIndex = props.cards.findIndex(
      (card) => !card.answer || typeof card.answer.answer !== 'number',
    );

    return firstUnansweredIndex === -1 ? 0 : firstUnansweredIndex;
  };

  const resetAnswer = (currentCard: CardI) => {
    return props.deleteAnswer(currentCard.id).catch((error: Error) => {
      logger.info('Error deleting answer', {
        error,
        info: {
          componentStack: 'AssessmentStream.jsx',
        },
      });
    });
  };

  const sendAnswer = (value: AnswerValue, currentCard: CardI, forceRating: boolean = true) => {
    const scale = props.scales[currentCard.question.scaleVersionId];
    const isMcq = scale.visualization === ScaleVisualization.McqOptions;

    const mcqRating = Array.isArray(value.rating)
      ? value.rating
      : [value.rating].filter((r): r is number => typeof r === 'number');

    const answer: AnswerI = {
      answer: isMcq ? null : mcqRating[0],
      multipleChoiceAnswers: isMcq ? mcqRating : undefined,
      comment: state.tmpComment,
      scaleVersionId: value.rating != null ? currentCard.question.scaleVersionId : null,
      forceRating,
    };

    if (value.comment) {
      answer.comment = value.comment.trim();
    }

    return props
      .updateCard(currentCard.id, answer)
      .then((rsp) => {
        if (!isSuccess(rsp)) {
          throw rsp.error.response?.data;
        }

        const obj = props.cards.find((card) => card.id === currentCard.id);
        if (obj && rsp.response) {
          obj.answer = rsp.response.data;
        }
        return rsp;
      })
      .catch((error: Error) => {
        logger.info('Error updating card', {
          error,
          info: {
            componentStack: 'AssessmentStream.tsx',
          },
        });
        throw error;
      });
  };

  const sleep = (time: number) => {
    return new Promise<void>((res) => {
      setTimeout(() => {
        res();
      }, time);
    });
  };

  const submit = async (value: AnswerValue, currentCard: CardI): Promise<void> => {
    await Promise.all([
      sleep(SUBMIT_MINIMUM_DELAY),
      sendAnswer(value, currentCard).then((rsl) => {
        setState({ tmpComment: '', commentChanged: false });

        return rsl;
      }),
    ]);
  };

  const finish = () => {
    props.finishQS(props.self ? PackName.SelfAssessment : PackName.Assessment);
  };

  const updateComment = (comment: string) => {
    setState({
      commentChanged: true,
      tmpComment: comment,
    });
  };

  const onLeaveCard = (index: number): Promise<unknown> | void => {
    if (!state.commentChanged) {
      return;
    }

    return sendAnswer({ comment: state.tmpComment, rating: null }, props.cards[index], false).then((rsl) => {
      setState({ tmpComment: '', commentChanged: false });

      return rsl;
    });
  };

  const { cardsLoading, cards, cycle } = props;

  return (
    <>
      <QuestionsWrapper>
        <QuestionsGrid>
          {props.cardsLoading || !props.cards.length ? (
            <LoadingCard />
          ) : (
            <>
              <Mobile>
                <PopoverInfo isSelfAssessment={props.self} isAnonymous={cycle.schedule.anonymous} />
              </Mobile>
              <StreamAnimation
                cards={props.cards}
                max={cards.length}
                startIndex={getStartIndex()}
                onFinish={finish}
                onLeave={onLeaveCard}
                onSubmit={submit}
                onChangeComment={updateComment}
              >
                {(nextCard, prevCard, currentIndex) => {
                  const handleSubmit = (value: AnswerValue, currentCard: CardI) => {
                    void submit(value, currentCard).then(() => {
                      nextCard();
                    });
                  };
                  return (
                    <QuestionCard
                      currentCard={cards[currentIndex]}
                      reset={resetAnswer}
                      onSubmit={handleSubmit}
                      saveLoading={cardsLoading}
                      nextCard={nextCard}
                      prevCard={prevCard}
                      showSpinner={false}
                      onChangeComment={updateComment}
                    />
                  );
                }}
              </StreamAnimation>
              <QuestionsInfo
                isSelfAssessment={props.self}
                dueDate={cycle.dueDate}
                count={cards.length}
                surveyName={translateContent(cycle.survey.name)}
                isAnonymous={cycle.schedule.anonymous}
              />
            </>
          )}
        </QuestionsGrid>
      </QuestionsWrapper>
      {!props.cardsLoading && props.cards.length && (
        <MobileTongue
          name={translateContent(cycle.survey.name)}
          surveyLength={cards.length}
          dueDate={cycle.dueDate}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: StoreI): StateProps => ({
  cards: getQuestions(state),
  scales: state.scales.byId,
  cardsLoading: state.user.questions.loading,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchCards: (id, filter, precedent) => dispatch(fetchQuestions(id, filter, precedent)),
  updateCard: (id, answer) => dispatch(updateQuestion(id, answer)),
  deleteAnswer: (id) => dispatch(deleteAnswer(id)),
});

const AssessmentStreamS = connect(mapStateToProps, mapDispatchToProps)(AssessmentStream);
export default AssessmentStreamS;
